import React, { useCallback } from 'react';
import { Form } from 'antd';
import { useSessionOutcome } from '@hooks/useSessionOutcome';
import ActionSubtypeFormItem from './ActionSubtypeFormItem';
import { ACTION_DISCHG_CODE, type SessionForm } from '@components/Clinicians/session-form';
import { useSessionAction } from '@hooks/useSessionAction';
import { useSessionFormState } from '@hooks/useSessionFormState';
import { DischargeReasonFormItem } from './DischargeReasonFormItem';

export const useValidateDischargeFields = () => {
  const form = Form.useFormInstance<SessionForm>();

  const { SUBTYPE_KEY, DISCHARGE_REASON_KEY } = useSessionAction(ACTION_DISCHG_CODE);

  const [dischargeSubtype] = useSessionFormState(SUBTYPE_KEY);
  const [dischargeReason] = useSessionFormState(DISCHARGE_REASON_KEY);

  const { selectedActions, appointmentVersion } = useSessionOutcome();

  const subTypeErrorMessage = 'Select a discharge type';
  const reasonErrorMessage = 'Select a discharge reason';

  return useCallback(() => {
    if (appointmentVersion !== 2) return [];
    if (!selectedActions.map(({ appt_action_code }) => appt_action_code).includes('DISCHG')) return [];

    const errors: string[] = [];

    if (!dischargeSubtype) {
      form.setFields([{ name: SUBTYPE_KEY, errors: [subTypeErrorMessage] }]);
      errors.push(subTypeErrorMessage);
    } else {
      form.setFields([{ name: SUBTYPE_KEY, errors: [] }]);
    }

    if (!dischargeReason && dischargeSubtype === 'DISCHARGE') {
      form.setFields([{ name: DISCHARGE_REASON_KEY, errors: [reasonErrorMessage] }]);
      errors.push(reasonErrorMessage);
    } else {
      form.setFields([{ name: DISCHARGE_REASON_KEY, errors: [] }]);
    }

    return errors;
  }, [appointmentVersion, dischargeReason, dischargeSubtype, form, selectedActions]);
};

const DischargeFields = () => {
  const { REASON_KEY, OTHER_REASON_KEY, COMMENT_KEY, SUBTYPE_KEY } = useSessionAction(ACTION_DISCHG_CODE);

  const [, setActionTaskComment] = useSessionFormState(COMMENT_KEY);
  const [, setReasonCode] = useSessionFormState(REASON_KEY);
  const [, setOtherReason] = useSessionFormState(OTHER_REASON_KEY);
  const [, setSubtype] = useSessionFormState(SUBTYPE_KEY);

  return (
    <>
      <ActionSubtypeFormItem
        actionCode={ACTION_DISCHG_CODE}
        onChange={(value) => {
          setReasonCode(undefined);
          setActionTaskComment(undefined);
          setOtherReason(undefined);
          setSubtype(value);
        }}
      />

      <DischargeReasonFormItem actionCode={ACTION_DISCHG_CODE} />
    </>
  );
};

export default DischargeFields;
